<script setup lang="ts">
import { computed, onBeforeUnmount, watch } from 'vue';
// eslint-disable-next-line import/no-cycle
import { useProductTourStore } from '@/stores/productTour';
import { usePermissions } from '@/composables/usePermissions';
import IconDashboard from '@/components/icons/IconDashboard.vue';
import DateRangePicker from '@/components/ui/DateRangePicker.vue';
import StatGroup from '@/components/ui/StatGroup.vue';
import PageView from '@/components/ui/PageView.vue';
import BarChart from '@/components/ui/BarChart.vue';
import DashboardStatPills from '@/components/dashboard/DashboardStatPills.vue';
import { useDashboardData } from '@/composables/useDashboardData';
import { useDashboardChartData } from '@/composables/useDashboardChartData';
import { formatDate, formatCurrency } from '@/utils/formatUtils';
import { useDateRangeWithTimezone } from '@/composables/useDateRangeWithTimezone';
import { PaymentStatus, type PaymentStatusValue } from '@/services/paymentStatus';
import type { StatItem, StatPill } from '@/types/StatGroup.types';

// PHASE2: Restore with the detailed Accounts and Negotiations stat groups.
// import IconCompletedNegotiations from '@/components/icons/IconCompletedNegotiations.vue';

const { can } = usePermissions();

const { selectedDateRange } = useDateRangeWithTimezone();

const formattedDateRange = computed<[string, string] | null>(() => {
    const range = selectedDateRange.value;
    if (!range) return null;
    return [formatDate(range[0]), formatDate(range[1])];
});

const paymentsLink = (tab: string, status?: PaymentStatusValue) => {
    const range = formattedDateRange.value;
    if (!range) return undefined;
    const statusParam = status ? `&status=${status}` : '';
    return `/payments?tab=${tab}${statusParam}&from=${range[0]}&to=${range[1]}`;
};

// PHASE1: Comment this block when restoring the complete PHASE2 data block below.
const {
    accountsData,
    accountsLoading,
    paymentsData,
    paymentsLoading,
} = useDashboardData(formattedDateRange, {
    loadAccounts: can('accounts'),
    loadNegotiations: false,
    loadPayments: can('payments'),
});

/*
 * PHASE2: Comment the PHASE1 data block above, then uncomment this complete block.
const {
    accountsData,
    accountsLoading,
    negotiationsData,
    negotiationsLoading,
    paymentsData,
    paymentsLoading,
} = useDashboardData(formattedDateRange, {
    loadAccounts: can('accounts'),
    loadNegotiations: can('negotiations'),
    loadPayments: can('payments'),
});
*/

const summaryStats = computed<StatItem[]>(() => {
    const stats: StatItem[] = [];

    if (can('accounts')) {
        stats.push({
            id: 'total-accounts',
            label: 'Total Accounts',
            value: accountsData.value?.totalAccounts ?? 0, // PHASE1: total_accounts direct from API
            // PHASE2: value: (accountsData.value?.verifiedAccounts ?? 0) + (accountsData.value?.pendingAccounts ?? 0),
            icon: 'fa-solid fa-users',
            // to: '/accounts',
        });
    }

    if (can('payments')) {
        stats.push(
            {
                id: 'cleared-txn',
                label: 'Total Cleared Transactions',
                value: paymentsData.value?.totalClearedTransactions ?? 0,
                icon: 'fa-solid fa-money-bill-transfer',
                to: paymentsLink('deposits'),
            },
            {
                id: 'cleared-amt',
                label: 'Total Cleared Amount',
                value: formatCurrency(paymentsData.value?.totalClearedAmount ?? 0),
                icon: 'fa-solid fa-circle-dollar-to-slot',
                to: paymentsLink('deposits'),
            },
        );
    }

    return stats;
});

const summaryLoading = computed(() => (
    (can('accounts') && accountsLoading.value)
    || (can('payments') && paymentsLoading.value)
));

/*
 * PHASE2: Restore the detailed stat groups without recreating their components.
const accountsStats = computed<StatItem[]>(() => [
    { id: 'verified-accounts', label: 'Verified Accounts', value: accountsData.value?.verifiedAccounts ?? 0, icon: 'fa-solid fa-user-check', to: '/accounts' },
    { id: 'pending-accounts', label: 'Pending Accounts', value: accountsData.value?.pendingAccounts ?? 0, icon: 'fa-solid fa-user-clock', to: '/accounts?tab=pending' },
]);

const negotiationsStats = computed<StatItem[]>(() => [
    { id: 'eligible', label: 'Total Eligible Accounts', value: negotiationsData.value?.eligibleAccounts ?? 0, icon: 'fa-solid fa-users', to: '/negotiations' },
    { id: 'pending', label: 'Total Pending Offers', value: negotiationsData.value?.pendingOffers ?? 0, icon: 'fa-solid fa-file-invoice-dollar', to: '/negotiations?tab=pending-offers' },
    { id: 'accepted', label: 'Total Accepted Offers', value: negotiationsData.value?.acceptedOffers ?? 0, icon: 'fa-solid fa-file-signature', to: '/negotiations?tab=accepted' },
    { id: 'rejected', label: 'Total Reject Offers', value: negotiationsData.value?.rejectedOffers ?? 0, icon: 'fa-solid fa-file-circle-xmark', to: '/negotiations?tab=rejected' },
    { id: 'completed', label: 'Total Completed Negotiations', value: negotiationsData.value?.completedNegotiations ?? 0, iconComponent: IconCompletedNegotiations, to: '/negotiations?tab=completed' },
]);

const paymentsStats = computed<StatItem[]>(() => [
    { id: 'cleared-txn', label: 'Total Cleared Transactions', value: paymentsData.value?.totalClearedTransactions ?? 0, icon: 'fa-solid fa-money-bill-transfer', to: paymentsLink('deposits') },
    { id: 'cleared-amt', label: 'Total Cleared Amount', value: formatCurrency(paymentsData.value?.totalClearedAmount ?? 0), icon: 'fa-solid fa-circle-dollar-to-slot', to: paymentsLink('deposits') },
]);
*/

const chartPills = computed<StatPill[]>(() => [
    { label: 'Pending', value: paymentsData.value?.pendingTransactions ?? 0, to: paymentsLink('other', PaymentStatus.PENDING) },
    { label: 'Open', value: paymentsData.value?.openTransactions ?? 0, to: paymentsLink('other', PaymentStatus.OPEN) },
    { label: 'Low Balance', value: paymentsData.value?.lowBalanceTransactions ?? 0, to: paymentsLink('other', PaymentStatus.LOW_BALANCE) },
]);

const {
    chartViewMode,
    chartData,
    chartOptions,
    openBucket,
    dailyAverage,
    chartDataLoading,
    rangeIncludesCurrentPeriod,
    dailyPaymentAverageLoading,
} = useDashboardChartData(formattedDateRange, { enabled: can('payments') });

const productTour = useProductTourStore();
watch(
    () => !!formattedDateRange.value && !summaryLoading.value
        && (!can('payments') || (!chartDataLoading.value && !dailyPaymentAverageLoading.value)),
    (ready) => { productTour.dashboardReady = ready; },
    { immediate: true, flush: 'post' },
);
onBeforeUnmount(() => { productTour.dashboardReady = false; });
</script>

<template>
    <PageView title="Dashboard" :icon="IconDashboard">
        <template #actions>
            <div data-product-tour="dashboard-date-range">
                <DateRangePicker v-model="selectedDateRange" triggerWidth="100%"/>
            </div>
        </template>

        <!-- PHASE1: Comment this complete block when restoring the PHASE2 dashboard below. -->
        <div class="dashboard__phase1-content">
            <StatGroup
                v-if="summaryStats.length"
                :items="summaryStats"
                :columns="summaryStats.length"
                :loading="summaryLoading"
            />

            <template v-if="can('payments')">
                <DashboardStatPills
                    :pills="chartPills"
                    :loading="paymentsLoading"
                />
                <BarChart
                    :daily-average="dailyAverage"
                    :chart-data="chartData"
                    :chart-options="chartOptions"
                    :loading="chartDataLoading || dailyPaymentAverageLoading"
                    :highlight-current-period="rangeIncludesCurrentPeriod"
                    clickable
                    @update:view-mode="chartViewMode = $event"
                    @bar-click="openBucket"
                />
            </template>

        </div>

        <!--
        PHASE2: Comment the PHASE1 template above, then uncomment this complete original dashboard.
        <div class="dashboard__content" :class="{ 'dashboard__content--single-col': !can('payments') }">
            <div v-if="can('payments')" class="dashboard__col">
                <StatGroup title="Payments" :items="paymentsStats" :columns="2" :loading="paymentsLoading" />
                <DashboardStatPills
                    :pills="chartPills"
                    :loading="paymentsLoading"
                />
                <BarChart
                    :daily-average="dailyAverage"
                    :chart-data="chartData"
                    :chart-options="chartOptions"
                    :loading="chartDataLoading || dailyPaymentAverageLoading"
                    :highlight-current-period="rangeIncludesCurrentPeriod"
                    clickable
                    @update:view-mode="chartViewMode = $event"
                    @bar-click="openBucket"
                />
            </div>
            <div class="dashboard__col">
                <StatGroup v-if="can('accounts')" title="Accounts" :items="accountsStats" :columns="2" :loading="accountsLoading" />
                <StatGroup v-if="can('negotiations')" title="Negotiations" :items="negotiationsStats" :columns="2" :loading="negotiationsLoading" />
            </div>
        </div>
        -->
    </PageView>
</template>

<style lang="scss" scoped>
.dashboard {
    // PHASE1: This can remain when PHASE2 is restored; it targets only PHASE1 markup.
    &__phase1-content {
        display: flex;
        flex-direction: column;
        gap: 20px;
    }

    /*
     * PHASE2: Restore the two-column layout with the detailed stat groups.
    &__content {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 24px;
        align-items: start;

        &--single-col {
            grid-template-columns: 1fr 1fr;

            .dashboard__col {
                grid-column: 1;
            }
        }
    }

    &__col {
        display: flex;
        flex-direction: column;
        gap: 24px;
    }
    */
}
</style>
